import {
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, timer } from 'rxjs';
import { EventPopoverTooltipComponent } from '../components/event-popover.component';

@UntilDestroy()
@Directive({
  selector: '[siatCustomToolTip]',
})
export class ToolTipRendererDirective implements OnInit, OnDestroy {
  /**
   * A boolean value indicating whether to show the tooltip.
   */
  @Input() showToolTip = true;

  /**
   * The text.
   */
  @Input(`siatCustomToolTip`) text: string;

  /**
   * The content template.
   */
  @Input() contentTemplate: TemplateRef<unknown>;

  /**
   * The content template context.
   */
  @Input() contentTemplateContext: unknown;

  /**
   * The overlay reference.
   */
  private _overlayRef: OverlayRef;

  /**
   * The injections.
   */
  private _overlay = inject(Overlay);
  private _overlayPositionBuilder = inject(OverlayPositionBuilder);
  private _elementRef = inject(ElementRef);

  /**
   * The hide timer subscription.
   */
  private _hideTimerSub: Subscription | null = null;

  /**
   * The hide delay in ms.
   */
  private readonly _hideDelay = 100;

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    if (!this.showToolTip) return;

    this._overlayRef = this._overlay.create({
      hasBackdrop: false,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    });

    this._overlayRef
      .backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.closeToolTip());

    this._overlayRef.overlayElement.addEventListener(
      'mouseenter',
      this.onOverlayMouseEnter
    );
    this._overlayRef.overlayElement.addEventListener(
      'mouseleave',
      this.onOverlayMouseLeave
    );
  }

  /**
   * When the mouse enters the element: position tooltip at mouse coordinates
   * so that it is always visible regardless of how tall the event is.
   */
  @HostListener('mouseenter', ['$event'])
  show(event: MouseEvent): void {
    if (event.buttons === 1) return;
    if (!this.showToolTip) return;
    this.cancelHideTimer();

    if (this._overlayRef && !this._overlayRef.hasAttached()) {
      const positionStrategy = this._overlayPositionBuilder
        .flexibleConnectedTo({ x: event.clientX, y: event.clientY })
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ])
        .withViewportMargin(8);

      this._overlayRef.updatePositionStrategy(positionStrategy);

      const tooltipRef: ComponentRef<EventPopoverTooltipComponent> =
        this._overlayRef.attach(
          new ComponentPortal(EventPopoverTooltipComponent)
        );

      tooltipRef.instance.text.set(this.text);
      tooltipRef.instance.contentTemplate.set(this.contentTemplate);
      tooltipRef.instance.context.set(this.contentTemplateContext);
    }
  }

  /**
   * When the mouse leaves the element
   */
  @HostListener('mouseleave')
  hide(): void {
    this.startHideTimer();
  }

  /**
   * Mouse enter tooltip.
   */
  private onOverlayMouseEnter = () => {
    this.cancelHideTimer();
  };

  /**
   * Mouse leave tooltip.
   */
  private onOverlayMouseLeave = () => {
    this.startHideTimer();
  };

  /**
   * Start the hide timer
   */
  private startHideTimer(): void {
    if (this._hideTimerSub) return;

    this._hideTimerSub = timer(this._hideDelay).subscribe(() =>
      this.closeToolTip()
    );
  }

  /**
   * Cancel the hide timer
   */
  private cancelHideTimer(): void {
    this._hideTimerSub?.unsubscribe();
    this._hideTimerSub = null;
  }

  /**
   * Close the tooltip
   */
  private closeToolTip(): void {
    this.cancelHideTimer();

    if (this._overlayRef && this._overlayRef.hasAttached()) {
      this._overlayRef.detach();
    }
  }

  /**
   * When the host is clicked.
   */
  @HostListener('mousedown')
  onHostInteraction(): void {
    this.closeToolTip();
  }

  /**
   * @inheritdoc
   */
  ngOnDestroy(): void {
    this.cancelHideTimer();
    if (this._overlayRef) {
      this._overlayRef.overlayElement.removeEventListener(
        'mouseenter',
        this.onOverlayMouseEnter
      );
      this._overlayRef.overlayElement.removeEventListener(
        'mouseleave',
        this.onOverlayMouseLeave
      );

      this.closeToolTip();
      this._overlayRef.dispose();
    }
  }
}
